import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { clientActions } from 'core/client/clientReducers'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useReactRouter from 'use-react-router'
import DocumentMeta from 'core/components/DocumentMeta'
import EntityYamlPage from 'k8s/components/common/entity/entity-yaml-page'
import ApiClient from 'api-client/ApiClient'
import jsYaml from 'js-yaml'
import { pathStrOr } from 'utils/fp'
import { routes } from 'core/utils/routes'
import { isEmpty } from 'ramda'
import useListAction from 'core/hooks/useListAction'
import useSelectorWithParams from 'core/hooks/useSelectorWithParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import {
  listVirtualMachines,
  listVirtualMachineInstances,
  updateVirtualMachine,
} from '../new-actions'
import { virtualMachinesSelector } from '../selectors'
import { IVirtualMachineDetailsPageTabs, IVirtualMachineSelector } from '../model'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import OverviewActions from 'core/elements/overview/OverviewActions'
import VirtualMachinePowerOperationDialog from '../VirtualMachinePowerOperationDialog'
import VmiPowerOperationDialog from '../VmiPowerOperationDialog'
import Overview from './Overview'
import { useSelector } from 'react-redux'
import { listClusters } from 'app/plugins/infrastructure/components/clusters/newActions'
import { clustersSelector } from 'app/plugins/infrastructure/components/clusters/selectors'
import { listServices } from 'k8s/components/services/new-actions'
import { serviceSelectors } from 'k8s/components/services/selectors'
import MigrateVmiDialog from './MigrateVmiDialog'
import DeleteVmDialog from '../DeleteVmDialog'
import MigrationStatus from './MigrationStatus'
import { listPods } from 'k8s/components/pods/new-actions'
import PollingData from 'core/components/PollingData'
import ActionsDropdown from 'core/elements/overview/ActionsDropdown'

const oneSecond = 1000
const { qbert } = ApiClient.getInstance()

const defaultVm = {} as IVirtualMachineSelector

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    gap: 32,
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
}))

const VirtualMachineDetailsPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id, clusterId, tab } = match.params
  const { reload: reloadVmis, loading: loadingVmis } = useListAction(listVirtualMachineInstances, {
    params: { clusterId },
  })
  const { loading, reload } = useListAction(listVirtualMachines, {
    params: { clusterId },
  })
  const vms = useSelectorWithParams(virtualMachinesSelector, { clusterId, useGlobalParams: false })
  const vm = useMemo(() => vms.find((vm) => vm.id === id) || defaultVm, [id, vms])

  const { reload: reloadPods, loading: loadingPods } = useListAction(listPods, {
    params: { clusterId, namespace: vm?.namespace },
  })

  const { update, updating, error } = useUpdateAction(updateVirtualMachine)

  const { loading: loadingClusters } = useListAction(listClusters)
  const clusters = useSelector(clustersSelector)

  const { loading: loadingServices } = useListAction(listServices, {
    params: {
      clusterId,
      namespace: 'kubevirt',
    },
    requiredParams: ['clusterId', 'namespace'],
  })
  const services = useSelectorWithParams(serviceSelectors, {
    clusterId,
    namespace: 'kubevirt',
    useGlobalParams: false,
  })

  const cluster = useMemo(() => clusters.find((c) => c?.uuid === vm?.clusterId), [clusters, vm])
  const service = useMemo(() => services.find((s) => s?.name === 'virtvnc'), [services])

  const reloadVms = useCallback(() => {
    reload(true, true)
    reloadVmis(true, true)
    reloadPods(true, true)
  }, [reload, reloadVmis, reloadPods])

  const actions = useMemo(
    () => ({
      leftActions: [
        {
          label: 'Refresh',
          icon: 'sync',
          onClick: reloadVms,
        },
      ],
      rightActions: [
        {
          label: 'Console',
          icon: 'terminal',
          disabledFn: (vm) => {
            return !vm?.vmi || loadingClusters || loadingServices || !service
          },
          tooltipFn: (vm) => {
            return vm?.vmi ? '' : 'A virtual machine instance is required to access the console'
          },
          // Todo: What if theres more than 1 port?
          // gray out if there's no VMI
          externalLink: `http://${cluster?.masterIp}:${service?.ports?.[0]?.nodePort}/vnc_lite.html?path=k8s/apis/subresources.kubevirt.io/v1/namespaces/${vm?.namespace}/virtualmachineinstances/${vm?.vmi?.name}/vnc`,
        },
      ],
    }),
    [reloadVms, vm, cluster, service, loadingClusters, loadingServices],
  )

  const dropdownActions = useMemo(
    () => [
      {
        label: 'Start',
        icon: 'play',
        DialogComponent: VirtualMachinePowerOperationDialog,
        refreshFn: reloadVms,
        disabledFn: (vm) =>
          vm?.status?.printableStatus === 'Running' || vm?.status?.printableStatus === 'Paused',
        tooltipFn: (vm, disabled) => {
          if (!disabled) {
            return ''
          }
          return 'VM not startable in this state'
        },
        customDialogProps: {
          params: {
            operation: 'start',
          },
        },
      },
      {
        label: 'Stop',
        icon: 'stop',
        DialogComponent: VirtualMachinePowerOperationDialog,
        refreshFn: reloadVms,
        disabledFn: (vm) => vm?.status?.printableStatus === 'Stopped',
        tooltipFn: (vm, disabled) => {
          if (!disabled) {
            return ''
          }
          return 'VM is already Stopped'
        },
        customDialogProps: {
          params: {
            operation: 'stop',
          },
        },
      },
      {
        label: 'Restart',
        icon: 'power-off',
        DialogComponent: VirtualMachinePowerOperationDialog,
        refreshFn: reloadVms,
        disabledFn: (vm) => vm?.status?.printableStatus !== 'Running',
        tooltipFn: (vm, disabled) => {
          if (!disabled) {
            return ''
          }
          return 'VM is only restartable in Running state'
        },
        customDialogProps: {
          params: {
            operation: 'restart',
          },
        },
      },
      {
        label: 'Pause',
        icon: 'pause',
        DialogComponent: VmiPowerOperationDialog,
        refreshFn: reloadVms,
        disabledFn: (vm) => {
          if (!vm?.vmi) {
            return true
          }
          return vm?.status?.printableStatus !== 'Running'
        },
        tooltipFn: (vm, disabled) => {
          if (!disabled) {
            return ''
          }
          return vm?.vmi
            ? 'VM may only be paused in Running state'
            : 'No virtual machine instance associated with this VM'
        },
        customDialogProps: {
          params: {
            operation: 'pause',
          },
        },
      },
      {
        label: 'Unpause',
        icon: 'play',
        DialogComponent: VmiPowerOperationDialog,
        refreshFn: reloadVms,
        disabledFn: (vm) => {
          if (!vm?.vmi) {
            return true
          }
          return vm?.status?.printableStatus !== 'Paused'
        },
        tooltipFn: (vm, disabled) => {
          if (!disabled) {
            return ''
          }
          return vm?.vmi
            ? 'VM may only be unpaused in Paused state'
            : 'No virtual machine instance associated with this VM'
        },
        customDialogProps: {
          params: {
            operation: 'unpause',
          },
        },
      },
      {
        label: 'Live Migration',
        icon: 'turn-up',
        disabledFn: (vm) => {
          const condition = vm?.vmi?.status?.conditions?.find(
            (cond) => cond.type === 'LiveMigratable',
          )
          return condition?.status === 'False'
        },
        tooltipFn: (vm, disabled) => {
          if (!disabled) {
            return ''
          }
          const condition = vm?.vmi?.status?.conditions?.find(
            (cond) => cond.type === 'LiveMigratable',
          )
          return condition?.status === 'False' ? condition?.message : ''
        },
        DialogComponent: MigrateVmiDialog,
      },
      {
        label: 'Delete VM',
        icon: 'trash-alt',
        DialogComponent: DeleteVmDialog,
      },
    ],
    [reloadVms, vm],
  )

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        clusterId: vm?.clusterName || clusterId,
        id: vm?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [vm?.clusterName, vm?.name, id, clusterId])

  const getVmYaml = useCallback(async () => {
    if (isEmpty(vm)) return undefined
    return qbert.getVirtualMachineByName(vm.clusterId, vm.namespace, vm.name)
  }, [vm])

  const handleVmUpdate = useCallback(
    async (yaml) => {
      const body = jsYaml.load(yaml)
      const namespace = pathStrOr('', 'metadata.namespace', body)
      const name = pathStrOr('', 'metadata.name', body)
      await update({
        clusterId,
        namespace,
        name,
        body,
      })
    },
    [vm],
  )

  const pollingCondition = useCallback(() => {
    return tab !== 'yaml'
  }, [tab])

  return (
    <>
      <DocumentMeta title="Virtual Machine Details" breadcrumbs />
      <PollingData
        hidden
        loading={loading}
        onReload={reload}
        refreshDuration={oneSecond * 120}
        pollingCondition={pollingCondition}
      />
      <PollingData
        hidden
        loading={loadingVmis}
        onReload={reloadVmis}
        refreshDuration={oneSecond * 120}
        pollingCondition={pollingCondition}
      />
      <PollingData
        hidden
        loading={loadingPods}
        onReload={reloadPods}
        refreshDuration={oneSecond * 120}
        pollingCondition={pollingCondition}
      />
      <div className={classes.grid}>
        <div className={classes.actions}>
          <OverviewActions<IVirtualMachineSelector> actions={actions} entity={vm} />
          <ActionsDropdown<IVirtualMachineSelector> actions={dropdownActions} entity={vm} />
        </div>
        <MigrationStatus vmi={vm?.vmi} />
        <div>
          <Tabs route={routes.virtualMachines.details}>
            <Tab label="Overview" value={IVirtualMachineDetailsPageTabs.Overview}>
              <Overview vm={vm} loading={loading} />
            </Tab>
            <Tab label="YAML" value={IVirtualMachineDetailsPageTabs.Yaml}>
              <EntityYamlPage
                entityType="VirtualMachine"
                entityName={vm?.name}
                getYamlFn={getVmYaml}
                handleUpdate={handleVmUpdate}
                loading={loading || updating}
                error={error}
              />
            </Tab>
          </Tabs>
        </div>
      </div>
    </>
  )
}

export default VirtualMachineDetailsPage
